import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Select,
  Tooltip,
  InputLabel,
} from "@mui/material";
import { useFetch } from "../../../services/hooks/useFetch";
import { apiPrefix } from "../../../utils/main";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import InfoIcon from "@mui/icons-material/Info";
import theme from "../../../styles/theme";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import { HTTPMethod } from "@fusionauth/typescript-client";

import {
  ProjectSettingName,
  RadiologyOptionValue,
  ProjectSettingDefaultValue,
  type ProjectSettingNameType,
  getMedicationsEnabled,
  getDocumentRepositoryEnabled,
  getProcedureTimelineEnabled,
  getCodeOffsetEnabled,
  getRadiologyImagesEnabled,
  getIncludeRadiology,
  getPathologyReportsEnabled,
} from "../../../models/ProjectSetting";
import { schemaIdentifier } from "../../../models/Source";
import { useNavigate } from "react-router-dom";

type SettingUpdateRequest = {
  reason: string;
  settings: Setting[];
};

type ProjectFlowsheetGroupsRequest = {
  reason: string;
  projectFlowsheetGroups: Partial<ProjectFlowsheetGroup>[];
};

type ProjectLabGroupsRequest = {
  reason: string;
  projectLabGroups: Partial<ProjectLabGroup>[];
};

type ProjectPromGroupsRequest = {
  reason: string;
  projectPromGroups: Partial<ProjectPromGroup>[];
};

const promFormTypes = [
  {
    id: "KOOS",
    name: "KOOS",
  },
  {
    id: "KOOSJR",
    name: "KOOS JR",
  },
  {
    id: "OXFORD",
    name: "Oxford",
  },
  {
    id: "SNOT",
    name: "SNOT",
  },
  {
    id: "PAIN_GLOBAL",
    name: "Global Pain",
  },
  {
    id: "PAIN_INTERFERENCE",
    name: "Pain Interference",
  },
  {
    id: "PAIN_SCREENING",
    name: "Pain Screening",
  },
  {
    id: "PROMIS_SOCIAL",
    name: "PROMIS Social",
  },
  {
    id: "PROMIS_COGNITIVE",
    name: "PROMIS Cognitive",
  },
  {
    id: "PROMIS_EMOTIONAL",
    name: "PROMIS Emotional",
  },
  {
    id: "PROMIS_PHYSICAL",
    name: "PROMIS Physical",
  },
  {
    id: "PROMIS_UPPER_EXTREMITY",
    name: "PROMIS Upper Extremity",
  },
];

const radiologyOptionDisplayValue = {
  [RadiologyOptionValue.none]: "Disabled",
  [RadiologyOptionValue.reports]: "Reports Only",
  [RadiologyOptionValue.images]: "Reports + Images",
};

const getBooleanDisplayValue = function (value: boolean) {
  return value ? "Enabled" : "Disabled";
};

type TooltipKey = ProjectSettingNameType | string;
const tooltipMessage: Partial<Record<TooltipKey, string>> = {
  [ProjectSettingName.codeOffsetEnabled]:
    "Enable or disable the the offset days for all diagnosis and procedure codes in this project.",
  [ProjectSettingName.documentRepositoryEnabled]:
    "Enable or disable documents related to specific orders.",
  [ProjectSettingName.includeRadiology]:
    "Select the radiology data to include in this project.",
  [ProjectSettingName.medicationsEnabled]:
    "Include medications in this project.",
  [ProjectSettingName.timelineEnabled]:
    "Enable or disable Procedure Timeline in this project.",
  PROM_GROUPS:
    "Select the types of Patient Reported Outcomes that you would like to include in this project. It may take a few minutes for this update to complete. Check Project Snapshot for progress and status.",
  FLOWSHEET_GROUPS:
    "Select the Flowsheet Groups that you would like to include in this project.",
  LAB_GROUPS:
    "Select the Lab Groups that you would like to include in this project.",
  [ProjectSettingName.pathologyReportsEnabled]:
    "Enable or disable Pathology Reports in this project.",
};

const getToolTipElement = function (
  projectSettingName: ProjectSettingNameType | string,
) {
  const message = tooltipMessage[projectSettingName];
  if (!message) return;

  return (
    <Tooltip
      title={message}
      placement="top-end"
      className="additional-data-tooltip"
    >
      <InfoIcon
        sx={{
          color: theme.palette.info.main,
        }}
      />
    </Tooltip>
  );
};

const formatOptionsFromFlowsheetGroupArray = (
  projectFlowsheetGroups: ProjectFlowsheetGroup[] | undefined,
  options: FlowsheetGroup[] | undefined,
) => {
  if (projectFlowsheetGroups && options) {
    return projectFlowsheetGroups
      .map((pFG) => {
        return options.find((o) => o.id === pFG.flowsheetGroupId);
      })
      .filter((fG) => {
        return typeof fG !== "undefined";
      });
  } else {
    return [];
  }
};

const formatOptionsFromLabGroupArray = (
  projectLabGroups: ProjectLabGroup[] | undefined,
  options: LabGroup[] | undefined,
) => {
  if (projectLabGroups && options) {
    return projectLabGroups
      .map((pLG) => {
        return options.find((o) => o.id === pLG.labGroupId);
      })
      .filter((lG) => {
        return typeof lG !== "undefined";
      });
  } else {
    return [];
  }
};

const formatOptionsFromPromGroupArray = (
  projectPromGroups: ProjectPromGroup[] | undefined,
  options: PromGroup[] | undefined,
) => {
  if (projectPromGroups && options) {
    return projectPromGroups
      .map((pPG) => {
        return options.find((o) => o.id === pPG.promGroupId);
      })
      .filter((pG) => {
        return typeof pG !== "undefined";
      });
  } else {
    return [];
  }
};

export default function AdditionalDataPage() {
  const project = useContext(ProjectContext);
  const navigate = useNavigate();
  const { id: projectId, isLocked, projectSettings, sourceSchema } = project!;
  const dispatch = useContext(ProjectDispatchContext);
  if (dispatch === null) {
    navigate("/");
  }
  const { fetch: fetchProjectSettings, update: bulkUpdateProjectSettings } =
    useFetch<ProjectSetting[], SettingUpdateRequest>();
  const {
    data: flowsheetGroupOptions,
    fetch: fetchFlowsheetGroups,
    isLoading: flowsheetGroupsLoading,
  } = useFetch<FlowsheetGroup[], undefined, PagedResponse<FlowsheetGroup>>();
  const {
    data: projectFlowsheetGroups,
    fetch: fetchProjectFlowsheetGroups,
    update: updateProjectFlowsheetGroups,
    isLoading: projectFlowsheetGroupsLoading,
  } = useFetch<ProjectFlowsheetGroup[], ProjectFlowsheetGroupsRequest>();
  const {
    data: labGroupOptions,
    fetch: fetchLabGroups,
    isLoading: labGroupsLoading,
  } = useFetch<LabGroup[], undefined, PagedResponse<LabGroup>>();
  const {
    data: projectLabGroups,
    fetch: fetchProjectLabGroups,
    update: updateProjectLabGroups,
    isLoading: projectLabGroupsLoading,
  } = useFetch<ProjectLabGroup[], ProjectLabGroupsRequest>();
  const {
    data: promGroupOptions,
    fetch: fetchPromGroups,
    isLoading: promGroupsLoading,
  } = useFetch<PromGroup[], undefined, PagedResponse<PromGroup>>();
  const {
    data: projectPromGroups,
    fetch: fetchProjectPromGroups,
    update: updateProjectPromGroups,
    isLoading: projectPromGroupsLoading,
  } = useFetch<ProjectPromGroup[], ProjectPromGroupsRequest>();

  const [codeOffsetEnabled, setCodeOffsetEnabled] = useState<boolean>(
    ProjectSettingDefaultValue[ProjectSettingName.codeOffsetEnabled],
  );
  const [medicationsEnabled, setMedicationsEnabled] = useState<boolean>(
    ProjectSettingDefaultValue[ProjectSettingName.medicationsEnabled],
  );
  const [documentRepositoryEnabled, setDocumentRepositoryEnabled] =
    useState<boolean>(
      ProjectSettingDefaultValue[ProjectSettingName.documentRepositoryEnabled],
    );
  const [timelineEnabled, setTimelineEnabled] = useState<boolean>(
    ProjectSettingDefaultValue[ProjectSettingName.timelineEnabled],
  );
  const [pathologyReportsEnabled, setPathologyReportsEnabled] =
    useState<boolean>(
      ProjectSettingDefaultValue[ProjectSettingName.pathologyReportsEnabled],
    );
  const [includeRadiology, setIncludeRadiology] =
    useState<RadiologyOptionValue>();
  const [flowsheetGroups, setFlowsheetGroups] = useState<FlowsheetGroup[]>([]);
  const [labGroups, setLabGroups] = useState<LabGroup[]>([]);
  const [promGroups, setPromGroups] = useState<PromGroup[]>([]);

  const [updateDetails, setUpdateDetails] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);

  const is215Project = sourceSchema === schemaIdentifier.mayo_dug_215;

  const handleSettingCreateOrUpdate = (
    settingName: string,
    newValue: string,
    forDelete: boolean,
  ) => {
    const existingSetting = projectSettings!.find(
      (s) => s.name === settingName,
    );

    return {
      id: existingSetting?.id || undefined,
      projectId: projectId,
      name: settingName,
      forDelete: forDelete,
      value: newValue,
    };
  };

  const handButtonClick = (isSave: boolean) => {
    if (isSave) {
      if (updateDetails === "") {
        setDisplayErrors(true);
        return;
      }

      const bulkSettings: Setting[] = [];

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.codeOffsetEnabled,
          codeOffsetEnabled ? "TRUE" : "FALSE",
          codeOffsetEnabled ===
            ProjectSettingDefaultValue[ProjectSettingName.codeOffsetEnabled],
        ),
      );

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.medicationsEnabled,
          medicationsEnabled ? "TRUE" : "FALSE",
          medicationsEnabled ===
            ProjectSettingDefaultValue[ProjectSettingName.medicationsEnabled],
        ),
      );

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.documentRepositoryEnabled,
          documentRepositoryEnabled ? "TRUE" : "FALSE",
          documentRepositoryEnabled ===
            ProjectSettingDefaultValue[
              ProjectSettingName.documentRepositoryEnabled
            ],
        ),
      );

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.timelineEnabled,
          timelineEnabled ? "TRUE" : "FALSE",
          timelineEnabled ===
            ProjectSettingDefaultValue[ProjectSettingName.timelineEnabled],
        ),
      );

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.includeRadiology,
          String(includeRadiology),
          includeRadiology ===
            ProjectSettingDefaultValue[ProjectSettingName.includeRadiology],
        ),
      );

      bulkSettings.push(
        handleSettingCreateOrUpdate(
          ProjectSettingName.pathologyReportsEnabled,
          pathologyReportsEnabled ? "TRUE" : "FALSE",
          pathologyReportsEnabled ===
            ProjectSettingDefaultValue[
              ProjectSettingName.pathologyReportsEnabled
            ],
        ),
      );

      void bulkUpdateProjectSettings({
        path: `${apiPrefix}/projects/${projectId}/settings/bulk`,
        body: {
          reason: updateDetails,
          settings: bulkSettings,
        },
        postResponseAction: () => {
          void fetchProjectSettings(
            `${apiPrefix}/projects/${projectId}/settings`,
            {
              postResponseAction: (settings) => {
                if (settings === undefined) return;
                dispatch({
                  type: "update",
                  project: {
                    projectSettings: settings,
                    documentRepositoryEnabled:
                      getDocumentRepositoryEnabled(settings),
                    medicationsEnabled: getMedicationsEnabled(settings),
                    radiologyImagesEnabled: getRadiologyImagesEnabled(settings),
                    procedureTimelineEnabled:
                      getProcedureTimelineEnabled(settings),
                    codeOffsetEnabled: getCodeOffsetEnabled(settings),
                  },
                });
              },
            },
          );
        },
      });

      void updateProjectFlowsheetGroups({
        path: `${apiPrefix}/projects/${projectId}/flowsheets`,
        method: HTTPMethod.POST,
        body: {
          reason: updateDetails,
          projectFlowsheetGroups: flowsheetGroups.map((value) => {
            return {
              projectId: projectId,
              flowsheetGroupId: value.id,
            };
          }),
        },
      });

      void updateProjectLabGroups({
        path: `${apiPrefix}/projects/${projectId}/labs`,
        method: HTTPMethod.POST,
        body: {
          reason: updateDetails,
          projectLabGroups: labGroups.map((value) => {
            return {
              projectId: projectId,
              labGroupId: value.id,
            };
          }),
        },
      });

      void updateProjectPromGroups({
        path: `${apiPrefix}/projects/${projectId}/proms`,
        method: HTTPMethod.POST,
        body: {
          reason: updateDetails,
          projectPromGroups: promGroups.map((value) => {
            return {
              projectId: projectId,
              promGroupId: value.id,
            };
          }),
        },
      });
    } else {
      refreshValuesFromCurrentProjectSettings();
    }
    setUpdateDetails("");
    setDisplayErrors(false);
    setEditMode(false);
  };

  const refreshValuesFromCurrentProjectSettings = useCallback(() => {
    if (!project || !projectSettings) return;

    setCodeOffsetEnabled(getCodeOffsetEnabled(projectSettings));
    setMedicationsEnabled(getMedicationsEnabled(projectSettings));
    setDocumentRepositoryEnabled(getDocumentRepositoryEnabled(projectSettings));
    setTimelineEnabled(getProcedureTimelineEnabled(projectSettings));
    setIncludeRadiology(getIncludeRadiology(projectSettings));
    setPathologyReportsEnabled(getPathologyReportsEnabled(projectSettings));
  }, [projectSettings, project]);

  useEffect(() => {
    if (projectSettings) {
      refreshValuesFromCurrentProjectSettings();
    }
  }, [projectSettings, refreshValuesFromCurrentProjectSettings]);

  useEffect(() => {
    if (!flowsheetGroupOptions && !flowsheetGroupsLoading) {
      void fetchFlowsheetGroups(
        `${apiPrefix}/flowsheets?sourceSchema=${sourceSchema}&limit=100`,
        { responseManipulationFunction: (data) => data.results ?? [] },
      );
    }
  }, [
    flowsheetGroupOptions,
    flowsheetGroupsLoading,
    fetchFlowsheetGroups,
    sourceSchema,
  ]);

  useEffect(() => {
    if (!projectFlowsheetGroups && !projectFlowsheetGroupsLoading) {
      void fetchProjectFlowsheetGroups(
        `${apiPrefix}/projects/${projectId}/flowsheets`,
      );
    }
  }, [
    projectFlowsheetGroups,
    projectFlowsheetGroupsLoading,
    fetchProjectFlowsheetGroups,
    projectId,
  ]);

  useEffect(() => {
    if (projectFlowsheetGroups) {
      setFlowsheetGroups(
        formatOptionsFromFlowsheetGroupArray(
          projectFlowsheetGroups,
          flowsheetGroupOptions,
        ),
      );
    }
  }, [projectFlowsheetGroups, flowsheetGroupOptions]);

  useEffect(() => {
    if (!labGroupOptions && !labGroupsLoading) {
      void fetchLabGroups(
        `${apiPrefix}/labs?sourceSchema=${sourceSchema}&limit=100`,
        { responseManipulationFunction: (data) => data.results ?? [] },
      );
    }
  }, [labGroupOptions, labGroupsLoading, fetchLabGroups, sourceSchema]);

  useEffect(() => {
    if (!projectLabGroups && !projectLabGroupsLoading) {
      void fetchProjectLabGroups(`${apiPrefix}/projects/${projectId}/labs`);
    }
  }, [
    projectLabGroups,
    projectLabGroupsLoading,
    fetchProjectLabGroups,
    projectId,
  ]);

  useEffect(() => {
    if (projectLabGroups) {
      setLabGroups(
        formatOptionsFromLabGroupArray(projectLabGroups, labGroupOptions),
      );
    }
  }, [projectLabGroups, labGroupOptions]);

  useEffect(() => {
    if (!promGroupOptions && !promGroupsLoading) {
      void fetchPromGroups(
        `${apiPrefix}/proms?sourceSchema=${sourceSchema}&limit=100`,
        { responseManipulationFunction: (data) => data.results ?? [] },
      );
    }
  }, [promGroupOptions, promGroupsLoading, fetchPromGroups, sourceSchema]);

  useEffect(() => {
    if (!projectPromGroups && !projectPromGroupsLoading) {
      void fetchProjectPromGroups(`${apiPrefix}/projects/${projectId}/proms`);
    }
  }, [
    projectPromGroups,
    projectPromGroupsLoading,
    fetchProjectPromGroups,
    projectId,
  ]);

  useEffect(() => {
    if (projectPromGroups) {
      setPromGroups(
        formatOptionsFromPromGroupArray(projectPromGroups, promGroupOptions),
      );
    }
  }, [projectPromGroups, promGroupOptions]);

  const getFormattedPromName = (promGroup: PromGroup) => {
    const formattedName = promFormTypes.find(
      (pft) => pft.id === promGroup.name,
    );

    if (formattedName) {
      return formattedName.name;
    } else {
      return promGroup.name;
    }
  };

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item additional-data-settings-page">
        <Grid>
          <Typography variant="h4">Additional Data</Typography>
        </Grid>
        <br />

        {is215Project && !editMode && (
          <>
            <Typography variant="caption" color="error">
              Some settings are not available for 2.15 schema projects
            </Typography>
          </>
        )}

        {/* Code Offset */}
        {!is215Project && (
          <>
            <Grid>
              {editMode ? (
                <>
                  <InputLabel shrink htmlFor="enable-code-offset">
                    Diagnosis and Procedure Code Offset Days
                    {getToolTipElement(ProjectSettingName.codeOffsetEnabled)}
                  </InputLabel>
                  <Select
                    sx={{ width: "50ch" }}
                    value={codeOffsetEnabled ? "TRUE" : "FALSE"}
                    onChange={(event) => {
                      setCodeOffsetEnabled(event.target.value === "TRUE");
                    }}
                    inputProps={{ id: "enable-code-offset" }}
                    variant="outlined"
                  >
                    <MenuItem value="FALSE">Disabled</MenuItem>
                    <MenuItem value="TRUE">Enabled</MenuItem>
                  </Select>
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Diagnosis and Procedure Code Offset Days
                    {getToolTipElement(ProjectSettingName.codeOffsetEnabled)}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {getBooleanDisplayValue(codeOffsetEnabled)}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Flowsheets */}
        {!is215Project && (
          <>
            <Grid>
              {editMode && !is215Project ? (
                <>
                  <InputLabel shrink htmlFor="select-flowsheets">
                    Flowsheet Groups
                    {getToolTipElement("FLOWSHEET_GROUPS")}
                  </InputLabel>
                  <Autocomplete
                    filterSelectedOptions
                    multiple
                    id={"select-flowsheets"}
                    className="additional-data-autocomplete"
                    loading={flowsheetGroupsLoading}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={flowsheetGroupOptions || []}
                    value={flowsheetGroups}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.id}
                    data-testid="AdditionalDataPage-autocompleteflowsheets"
                    onChange={(_event, value) => {
                      setFlowsheetGroups(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="medium"
                        variant="outlined"
                        placeholder={flowsheetGroups.length ? "" : "Disabled"}
                      />
                    )}
                    sx={{ width: "50ch" }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Flowsheet Groups
                    {getToolTipElement("FLOWSHEET_GROUPS")}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {flowsheetGroups && flowsheetGroups.length
                      ? flowsheetGroups.map((fg) => fg.name).join(", ")
                      : "Disabled"}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Labs */}
        {!is215Project && (
          <>
            <Grid>
              {editMode && !is215Project ? (
                <>
                  <InputLabel shrink htmlFor="select-labs">
                    Lab Groups
                    {getToolTipElement("LAB_GROUPS")}
                  </InputLabel>
                  <Autocomplete
                    filterSelectedOptions
                    multiple
                    id={"select-labs"}
                    className="additional-data-autocomplete"
                    loading={labGroupsLoading}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    options={labGroupOptions || []}
                    value={labGroups}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.id}
                    data-testid="AdditionalDataPage-autocompletelabs"
                    onChange={(_event, value) => {
                      setLabGroups(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="medium"
                        variant="outlined"
                        placeholder={labGroups.length ? "" : "Disabled"}
                      />
                    )}
                    sx={{ width: "50ch" }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Lab Groups
                    {getToolTipElement("LAB_GROUPS")}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {labGroups && labGroups.length
                      ? labGroups.map((lg) => lg.name).join(", ")
                      : "Disabled"}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Medications */}
        {!is215Project && (
          <>
            <Grid>
              {editMode ? (
                <>
                  <InputLabel shrink htmlFor="enable-medications">
                    Medications
                    {getToolTipElement(ProjectSettingName.medicationsEnabled)}
                  </InputLabel>
                  <Select
                    sx={{ width: "50ch" }}
                    value={medicationsEnabled ? "TRUE" : "FALSE"}
                    onChange={(event) => {
                      setMedicationsEnabled(event.target.value === "TRUE");
                    }}
                    inputProps={{ id: "enable-medications" }}
                    variant="outlined"
                  >
                    <MenuItem value="FALSE">Disabled</MenuItem>
                    <MenuItem value="TRUE">Enabled</MenuItem>
                  </Select>
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Medications
                    {getToolTipElement(ProjectSettingName.medicationsEnabled)}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {getBooleanDisplayValue(medicationsEnabled)}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Document Repository */}
        <Grid>
          {editMode ? (
            <>
              <InputLabel shrink htmlFor="enable-medications">
                Order level Documents/PDFs
                {getToolTipElement(
                  ProjectSettingName.documentRepositoryEnabled,
                )}
              </InputLabel>
              <Select
                sx={{ width: "50ch" }}
                value={documentRepositoryEnabled ? "TRUE" : "FALSE"}
                onChange={(event) => {
                  setDocumentRepositoryEnabled(event.target.value === "TRUE");
                }}
                inputProps={{ id: "enable-documentrepository" }}
                variant="outlined"
              >
                <MenuItem value="FALSE">Disabled</MenuItem>
                <MenuItem value="TRUE">Enabled</MenuItem>
              </Select>
            </>
          ) : (
            <>
              <Typography variant="caption">
                Order level Documents/PDFs
                {getToolTipElement(
                  ProjectSettingName.documentRepositoryEnabled,
                )}
              </Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {getBooleanDisplayValue(documentRepositoryEnabled)}
              </Typography>
            </>
          )}
        </Grid>
        <br />

        {/* Pathology Reports */}
        {!is215Project && (
          <>
            <Grid>
              {editMode && !is215Project ? (
                <>
                  <InputLabel shrink htmlFor="enable-pathology-reports">
                    Pathology Reports
                    {getToolTipElement(
                      ProjectSettingName.pathologyReportsEnabled,
                    )}
                  </InputLabel>
                  <Select
                    sx={{ width: "50ch" }}
                    value={pathologyReportsEnabled ? "TRUE" : "FALSE"}
                    onChange={(event) => {
                      setPathologyReportsEnabled(event.target.value === "TRUE");
                    }}
                    inputProps={{ id: "enable-pathology-reports" }}
                    variant="outlined"
                  >
                    <MenuItem value="FALSE">Disabled</MenuItem>
                    <MenuItem value="TRUE">Enabled</MenuItem>
                  </Select>
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Pathology Reports
                    {getToolTipElement(
                      ProjectSettingName.pathologyReportsEnabled,
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {getBooleanDisplayValue(pathologyReportsEnabled)}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* PROM */}
        <Grid>
          {editMode ? (
            <>
              <InputLabel shrink htmlFor="select-proms">
                Patient Reported Outcomes
                {getToolTipElement("PROM_GROUPS")}
              </InputLabel>
              <Autocomplete
                filterSelectedOptions
                multiple
                id={"select-proms"}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={promGroupOptions || []}
                loading={promGroupsLoading}
                value={promGroups}
                getOptionLabel={(option) => getFormattedPromName(option)}
                getOptionKey={(option) => option.id}
                data-testid="AdditionalDataPage-autocompleteforms"
                onChange={(_event, value) => {
                  setPromGroups(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    variant="outlined"
                    placeholder={promGroups.length ? "" : "Disabled"}
                  />
                )}
                sx={{ width: "50ch" }}
              />
            </>
          ) : (
            <>
              <Typography variant="caption">
                Patient Reported Outcomes
                {getToolTipElement("PROM_GROUPS")}
              </Typography>
              <Typography variant="body1" sx={{ width: "50ch" }}>
                {promGroups && promGroups.length
                  ? promGroups.map((pG) => getFormattedPromName(pG)).join(", ")
                  : "Disabled"}
              </Typography>
            </>
          )}
        </Grid>
        <br />

        {/* Procedure Timeline */}
        {!is215Project && (
          <>
            <Grid>
              {editMode && !is215Project ? (
                <>
                  <InputLabel shrink htmlFor="enable-timeline">
                    Procedure Timeline
                    {getToolTipElement(ProjectSettingName.timelineEnabled)}
                  </InputLabel>
                  <Select
                    sx={{ width: "50ch" }}
                    value={timelineEnabled ? "TRUE" : "FALSE"}
                    onChange={(event) => {
                      setTimelineEnabled(event.target.value === "TRUE");
                    }}
                    inputProps={{ id: "enable-timeline" }}
                    variant="outlined"
                  >
                    <MenuItem value="FALSE">Disabled</MenuItem>
                    <MenuItem value="TRUE">Enabled</MenuItem>
                  </Select>
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Procedure Timeline
                    {getToolTipElement(ProjectSettingName.timelineEnabled)}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {getBooleanDisplayValue(timelineEnabled)}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Radiology */}
        {!is215Project && (
          <>
            <Grid>
              {editMode && !is215Project ? (
                <>
                  <InputLabel shrink htmlFor="select-radiologyEnabled">
                    Radiology Reports and Images
                    {getToolTipElement(ProjectSettingName.includeRadiology)}
                  </InputLabel>
                  <Select<RadiologyOptionValue>
                    sx={{ width: "50ch" }}
                    value={includeRadiology}
                    onChange={(event) => {
                      setIncludeRadiology(event.target.value);
                    }}
                    inputProps={{ id: "select-radiologyEnabled" }}
                    variant="outlined"
                  >
                    {Object.values(RadiologyOptionValue).map((optionValue) => (
                      <MenuItem key={optionValue} value={optionValue}>
                        {radiologyOptionDisplayValue[optionValue]}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : (
                <>
                  <Typography variant="caption">
                    Radiology Reports and Images
                    {getToolTipElement(ProjectSettingName.includeRadiology)}
                  </Typography>
                  <Typography variant="body1" sx={{ width: "50ch" }}>
                    {includeRadiology !== undefined
                      ? radiologyOptionDisplayValue[includeRadiology]
                      : ""}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}

        {/* Reason */}
        {editMode && (
          <>
            <Grid>
              <TextField
                required
                variant="outlined"
                label="Note/Reason for Updating the Additional Data"
                className="settings-text-input"
                sx={{ width: "50ch" }}
                value={updateDetails}
                error={displayErrors && updateDetails === ""}
                helperText={
                  displayErrors && updateDetails === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setUpdateDetails(event.target.value);
                }}
              />
            </Grid>
            <br />
          </>
        )}
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button variant="outlined" onClick={() => handButtonClick(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={() => handButtonClick(true)}
              >
                Update
              </Button>
            </>
          )) || (
            <CloseArrowTooltip
              title={isLocked ? "This project is locked." : ""}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={() => setEditMode(true)}
                  disabled={isLocked}
                >
                  Edit
                </Button>
              </span>
            </CloseArrowTooltip>
          )}
        </Grid>
      </div>
    </Grid>
  );
}
